import { useLazyQuery } from "@apollo/client";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAppContext } from "../../../../classes/contexts/AppContext";
import { usePatientContext } from "../../../../classes/contexts/PatientContext";
import { useUser } from "../../../../classes/contexts/UserContext";
import { DataPermissions } from "../../../../classes/enums/PermissionsEnum";
import {
  filterByDateRange,
  formatDisplayDate,
  parseYYYYMMDD,
} from "../../../../classes/utils/DateUtils";
import { filterBySearchTerm } from "../../../../classes/utils/SearchUtils";
import SearchAndFilterBar from "../../../../components/filterAndActionBar.js/FilterAndActionBar";
import DoubleArrowIcon from "../../../../components/icons/DoubleArrowIcon";
import DownArrowIcon from "../../../../components/icons/DownArrowIcon";
import UpArrowIcon from "../../../../components/icons/UpArrowIcon";
import WidgetLoader from "../../../../components/widgetLoader/WidgetLoader";
import useTranslation from "../../../../hooks/i18n/useTranslation";
import { useAuth } from "../../../../hooks/useAuth";
import { GET_INSTANCES_BY_IDS_QUERY } from "../../../../queries/instanceQueries";
import { GET_SERIES_BY_ID_QUERY } from "../../../../queries/seriesQueries";
import { GET_STUDIES_LIST_BY_IDS_QUERY } from "../../../../queries/studiesQueries";
import FileImport from "../../../common/uploadModal/fileImport/FileImport";
import SeriesItem from "./seriesItem/SeriesItem";

import AddNew from "../../../../components/addNew/AddNew";
import "../common/PatientWidgetLayout.css";
import usePatientWidgetControlsOffset from "../common/usePatientWidgetControlsOffset";
import "./MedicalImage.css";

const MedicalImage = () => {
  const { t } = useTranslation();
  const { patientId, patientData } = usePatientContext();
  const { showPopup } = useAppContext();
  const { checkPermissions } = useAuth();
  const { dateSettings } = useUser();

  const [getPatientStudiesData, { data: studiesData }] = useLazyQuery(
    GET_STUDIES_LIST_BY_IDS_QUERY,
    {
      fetchPolicy: "network-only",
    }
  );

  const [getSeriesData] = useLazyQuery(GET_SERIES_BY_ID_QUERY, {
    fetchPolicy: "network-only",
  });

  const [getInstancesData, { data: instancesData }] = useLazyQuery(
    GET_INSTANCES_BY_IDS_QUERY,
    {
      fetchPolicy: "network-only",
    }
  );

  const [searchTerm, setSearchTerm] = useState("");
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [sortColumn, setSortColumn] = useState("creation_date");
  const [sortDirection, setSortDirection] = useState("desc");
  const [selectedInstanceId, setSelectedInstanceId] = useState(null);
  const [allInstances, setAllInstances] = useState([]);
  const [allSeries, setAllSeries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [seriesInstanceMap, setSeriesInstanceMap] = useState({});

  const columns = [
    { tradId: "59", property: "modality" },
    { tradId: "61", property: "creation_date" },
  ];

  const canImport = checkPermissions(DataPermissions.IMPORT_MEDICAL_IMAGE);

  useEffect(() => {
    setAllInstances([]);
    setAllSeries([]);
    setSeriesInstanceMap({});
    setIsLoading(true);
  }, [patientId]);

  useEffect(() => {
    if (patientData) {
      getPatientStudiesData({
        variables: { ids: patientData.studies },
      });
    }
  }, [patientData, getPatientStudiesData]);

  useEffect(() => {
    const fetchAllInstances = async () => {
      if (!studiesData?.studiesByIds) return;

      const allInstanceIds = [];
      const seriesDataMap = {};
      const seriesInstanceMapping = {};
      setIsLoading(true);

      const allSeriesIds = studiesData.studiesByIds.reduce((acc, study) => {
        return [...acc, ...study.series_ids];
      }, []);

      for (const seriesId of allSeriesIds) {
        try {
          const { data: seriesData } = await getSeriesData({
            variables: { id: seriesId },
          });

          if (seriesData?.seriesById) {
            seriesDataMap[seriesId] = seriesData.seriesById;
            if (seriesData.seriesById.instance_ids) {
              seriesInstanceMapping[seriesId] =
                seriesData.seriesById.instance_ids;
              allInstanceIds.push(...seriesData.seriesById.instance_ids);
            }
          }
        } catch (error) {}
      }

      setAllSeries(Object.values(seriesDataMap));
      setSeriesInstanceMap(seriesInstanceMapping);

      if (allInstanceIds.length > 0) {
        getInstancesData({
          variables: { ids: allInstanceIds },
        });
      }

      setIsLoading(false);
    };

    fetchAllInstances();
  }, [studiesData, getSeriesData, getInstancesData]);

  useEffect(() => {
    if (instancesData?.instancesByIds) {
      const patientId = patientData?._id;
      const filteredInstances = patientId
        ? instancesData.instancesByIds.filter(
            instance => instance.patient_id === patientId
          )
        : instancesData.instancesByIds;

      setAllInstances(filteredInstances);
    }
  }, [instancesData, patientData?._id]);

  const refreshInstances = useCallback(async () => {
    if (patientData) {
      const { data: newStudiesData } = await getPatientStudiesData({
        variables: { ids: patientData.studies },
      });

      if (newStudiesData?.studiesByIds) {
        const allInstanceIds = [];
        const seriesDataMap = {};
        const seriesInstanceMapping = {};

        const allSeriesIds = newStudiesData.studiesByIds.reduce(
          (acc, study) => {
            return [...acc, ...study.series_ids];
          },
          []
        );

        for (const seriesId of allSeriesIds) {
          try {
            const { data: seriesData } = await getSeriesData({
              variables: { id: seriesId },
            });

            if (seriesData?.seriesById) {
              seriesDataMap[seriesId] = seriesData.seriesById;

              if (seriesData.seriesById.instance_ids) {
                seriesInstanceMapping[seriesId] =
                  seriesData.seriesById.instance_ids;
                allInstanceIds.push(...seriesData.seriesById.instance_ids);
              }
            }
          } catch (error) {}
        }

        setAllSeries(Object.values(seriesDataMap));
        setSeriesInstanceMap(seriesInstanceMapping);

        if (allInstanceIds.length > 0) {
          getInstancesData({
            variables: { ids: allInstanceIds },
          });
        }
      }
    }
    clearFilters();
  }, [patientData, getPatientStudiesData, getSeriesData, getInstancesData]);

  const handleSearch = term => {
    setSearchTerm(term);
    setSelectedInstanceId(null);
  };

  const handleDateRangeChange = (start, end) => {
    setDateRange({ start, end });
    setSelectedInstanceId(null);
  };

  const clearFilters = () => {
    setSearchTerm("");
    setDateRange({ start: null, end: null });
    setSelectedInstanceId(null);
    setSortColumn("creation_date");
    setSortDirection("desc");
  };

  const handleSort = column => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("desc");
    }

    setSelectedInstanceId(null);
  };

  const openFileUploadModal = () => {
    showPopup(
      <FileImport
        onSave={processedGroups => {
          refreshInstances();
        }}
        allowedFileTypes={[".dcm", ".dicom", ".nii", ".nii.gz"]}
      />
    );
  };

  const renderFilter = () => (
    <SearchAndFilterBar
      onSearch={handleSearch}
      onDateRangeChange={handleDateRangeChange}
      onClear={clearFilters}
      onAdd={canImport ? openFileUploadModal : null}
      value={searchTerm}
    />
  );

  const renderContent = () => {
    if (isLoading) {
      return <WidgetLoader isLoading={true} />;
    }

    if (groupedBySeries.length > 0) {
      return (
        <div className="medical-image-instances instances-list">
          {groupedBySeries.map(seriesGroup => (
            <SeriesItem
              key={seriesGroup.series._id}
              seriesData={seriesGroup.series}
              instances={seriesGroup.instances}
              isExpandedByDefault={seriesGroup.isExpandedByDefault}
              onInstanceDeleted={refreshInstances}
            />
          ))}
        </div>
      );
    }

    if (canImport) {
      return (
        <AddNew elementTitle="Medical Image" onClick={openFileUploadModal} />
      );
    }

    return <div className="medical-image-empty" />;
  };

  const { controlsRef, shellStyle } = usePatientWidgetControlsOffset();

  const sortedInstances = useMemo(() => {
    if (!allInstances || allInstances.length === 0) return [];

    let filteredInstances = filterBySearchTerm(
      allInstances,
      instance =>
        `${instance.modality || ""} ${instance.file_name || ""} ${
          formatDisplayDate(instance.creation_date, {
            locale: dateSettings?.locale,
            format: dateSettings?.format,
          }) || ""
        }`,
      searchTerm
    );

    filteredInstances = filterByDateRange(
      filteredInstances,
      instance => instance.creation_date,
      dateRange.start,
      dateRange.end
    );

    if (selectedInstanceId) {
      const selectedInstance = filteredInstances.find(
        instance => instance._id === selectedInstanceId
      );
      if (selectedInstance) {
        filteredInstances = [
          selectedInstance,
          ...filteredInstances.filter(
            instance => instance._id !== selectedInstanceId
          ),
        ];
      }
    } else if (sortColumn) {
      filteredInstances.sort((a, b) => {
        let valueA = a[sortColumn];
        let valueB = b[sortColumn];

        if (sortColumn === "creation_date") {
          valueA = parseYYYYMMDD(valueA);
          valueB = parseYYYYMMDD(valueB);

          if (!valueA || !valueB) {
            valueA = a[sortColumn];
            valueB = b[sortColumn];
          }
        }

        const multiplier = sortDirection === "asc" ? 1 : -1;
        if (valueA < valueB) return -1 * multiplier;
        if (valueA > valueB) return 1 * multiplier;
        return 0;
      });
    }

    return filteredInstances;
  }, [
    allInstances,
    searchTerm,
    dateRange,
    selectedInstanceId,
    sortColumn,
    sortDirection,
    dateSettings,
  ]);

  const groupedBySeries = useMemo(() => {
    if (
      !allSeries ||
      allSeries.length === 0 ||
      !seriesInstanceMap ||
      !sortedInstances
    ) {
      return [];
    }

    let seriesWithInstances = [];

    allSeries.forEach(series => {
      const instanceIds = seriesInstanceMap[series._id] || [];
      const instances = sortedInstances.filter(instance =>
        instanceIds.includes(instance._id)
      );

      if (instances.length === 0) return;

      // Group instances by modality within the same series
      const instancesByModality = instances.reduce((acc, instance) => {
        const modality = instance.modality || "UNKNOWN";
        if (!acc[modality]) {
          acc[modality] = [];
        }
        acc[modality].push(instance);
        return acc;
      }, {});

      // Create a separate group for each modality
      Object.entries(instancesByModality).forEach(
        ([modality, modalityInstances]) => {
          seriesWithInstances.push({
            series: {
              ...series,
              _id: `${series._id}_${modality}`, // Create unique ID for each modality group
              modality: modality, // Override series modality with instance modality
            },
            instances: modalityInstances,
            isExpandedByDefault: seriesWithInstances.length === 0,
          });
        }
      );
    });

    seriesWithInstances = seriesWithInstances.filter(
      item => item.instances.length > 0
    );

    if (sortColumn && seriesWithInstances.length > 0) {
      seriesWithInstances.sort((a, b) => {
        let valueA, valueB;

        if (sortColumn === "modality") {
          valueA = a.series?.modality || a.instances[0]?.modality || "";
          valueB = b.series?.modality || b.instances[0]?.modality || "";
        } else if (sortColumn === "creation_date") {
          const getDatesFromInstances = instances => {
            const dates = instances
              .map(instance => parseYYYYMMDD(instance.creation_date))
              .filter(Boolean);
            return dates.length > 0 ? Math.min(...dates) : 0;
          };

          valueA = getDatesFromInstances(a.instances);
          valueB = getDatesFromInstances(b.instances);
        } else {
          return 0;
        }

        const multiplier = sortDirection === "asc" ? 1 : -1;

        if (sortColumn === "creation_date") {
          return (valueA - valueB) * multiplier;
        } else {
          return valueA.localeCompare(valueB) * multiplier;
        }
      });
    }

    return seriesWithInstances;
  }, [
    allSeries,
    seriesInstanceMap,
    sortedInstances,
    sortColumn,
    sortDirection,
  ]);

  return (
    <div
      className="medical-image-container patient-widget-shell"
      style={shellStyle}
    >
      <div className="medical-image-content patient-widget-content">
        {allSeries.length > 0 && (
          <div className="istance-header medical-image-header">
            {columns.map((col, index) => (
              <div
                key={index}
                className="istance-header-item"
                onClick={() => handleSort(col.property)}
              >
                <span>{t(col.tradId)}</span>
                <div className="App-spinesim-study-arrow-icon">
                  {col.property === sortColumn ? (
                    sortDirection === "asc" ? (
                      <UpArrowIcon />
                    ) : (
                      <DownArrowIcon />
                    )
                  ) : (
                    <DoubleArrowIcon />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {renderContent()}
      </div>

      <div
        className="filter-container patient-widget-controls"
        ref={controlsRef}
      >
        {renderFilter()}
      </div>
    </div>
  );
};

export default MedicalImage;
